/**
 * ============================================================
 * BOHO TALES — YOU MAY ALSO LIKE
 * ============================================================
 *
 * FINAL LOGIC
 *
 * No active Focus:
 *   Shopify recommendations
 *
 * Active Focus:
 *   Focus products
 *   +
 *   Shopify recommendations
 *
 * Then:
 *   - current PDP removed
 *   - duplicates removed
 *   - Focus products placed first
 *   - list trimmed to configured maximum
 *
 * Existing Shopify/Liquid _product-card is used for rendering.
 * ============================================================
 */


(function () {

  const FOCUS_DURATION = 30 * 60 * 1000;


  /* =========================================================
     START
     ========================================================= */

  function init() {

    const sections = document.querySelectorAll(
      '[data-boho-yml]:not([data-boho-yml-initialized])'
    );

    sections.forEach((section) => {

      section.setAttribute(
        'data-boho-yml-initialized',
        'true'
      );

      loadRecommendations(section);

    });

  }



  /* =========================================================
     MAIN RECOMMENDATION FLOW
     ========================================================= */

  async function loadRecommendations(section) {

    const currentProductId =
      String(section.dataset.productId || '');

    const sectionId =
      section.dataset.sectionId;

    const maxProducts =
      Number(section.dataset.maxProducts || 4);

    const recommendationsUrl =
      section.dataset.recommendationsUrl;


    const loader =
      section.querySelector('[data-boho-yml-loader]');

    const finalGrid =
      section.querySelector('[data-boho-yml-final-grid]');


    if (
      !currentProductId ||
      !sectionId ||
      !recommendationsUrl ||
      !finalGrid
    ) {

      console.warn(
        'BOHO YML — missing required section data.'
      );

      if (loader) loader.hidden = true;

      return;

    }


    try {

      /* -----------------------------------------------------
         1. Shopify's normal recommendations
         ----------------------------------------------------- */

      const shopifyCards =
        await getShopifyRecommendations({
          recommendationsUrl,
          currentProductId,
          sectionId,
          maxProducts
        });


      /* -----------------------------------------------------
         2. Read Focus journey
         ----------------------------------------------------- */

      const focusData =
        getActiveFocus();


      let focusCards = [];


      /* -----------------------------------------------------
         3. If Focus exists, resolve products belonging to tag
         ----------------------------------------------------- */

      if (focusData) {

        const focusProducts =
          await getFocusProducts(
            focusData.collectionHandle,
            focusData.tag,
            currentProductId
          );


        /* ---------------------------------------------------
           4. Render each Focus product through Liquid
           --------------------------------------------------- */

        const limitedFocusProducts =
          focusProducts.slice(0, maxProducts);


        focusCards =
          await Promise.all(
            limitedFocusProducts.map(
              (focusProduct) =>
                renderProductCard(
                  focusProduct,
                  sectionId
                )
            )
          );


        focusCards =
          focusCards.filter(Boolean);

      }


      /* -----------------------------------------------------
         5. Merge

         Focus always comes first.
         Shopify fills remaining positions.
         ----------------------------------------------------- */

      const finalCards =
        mergeCards({
          focusCards,
          shopifyCards,
          currentProductId,
          maxProducts
        });


      /* -----------------------------------------------------
         6. Display
         ----------------------------------------------------- */

      finalGrid.innerHTML = '';


      finalCards.forEach((card) => {

        finalGrid.appendChild(
          card.cloneNode(true)
        );

      });


      if (loader) {
        loader.hidden = true;
      }


      if (finalCards.length) {

        finalGrid.hidden = false;

      } else {

        /*
         If Shopify genuinely returns nothing and Focus
         also returns nothing, don't leave an empty section.
        */

        section.hidden = true;

      }


      console.log(
        'BOHO YML — final recommendations:',
        {
          focus: focusData,
          focusCount: focusCards.length,
          shopifyCount: shopifyCards.length,
          finalCount: finalCards.length
        }
      );


    } catch (error) {

      console.error(
        'BOHO YML — recommendation loading failed:',
        error
      );


      if (loader) {
        loader.hidden = true;
      }

    }

  }



  /* =========================================================
     SHOPIFY RECOMMENDATIONS
     ========================================================= */

  async function getShopifyRecommendations({
    recommendationsUrl,
    currentProductId,
    sectionId,
    maxProducts
  }) {

    const url =
      `${recommendationsUrl}` +
      `?product_id=${encodeURIComponent(currentProductId)}` +
      `&limit=${encodeURIComponent(maxProducts)}` +
      `&section_id=${encodeURIComponent(sectionId)}` +
      `&intent=related`;


    const response =
      await fetch(url, {
        credentials: 'same-origin'
      });


    if (!response.ok) {

      throw new Error(
        `Shopify recommendations failed: ${response.status}`
      );

    }


    const html =
      await response.text();


    const doc =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );


    return Array.from(
      doc.querySelectorAll(
        '[data-boho-yml-shopify-results] [data-boho-yml-card]'
      )
    );

  }



  /* =========================================================
     READ ACTIVE FOCUS
     ========================================================= */

  function getActiveFocus() {

    try {

      const journeyRaw =
        sessionStorage.getItem(
          'bohotales_active_focus_collection'
        );


      if (!journeyRaw) {
        return null;
      }


      let journey;


      try {

        journey =
          JSON.parse(journeyRaw);

      } catch {

        journey = {
          collection: journeyRaw
        };

      }


      const collectionHandle =
        journey?.collection ||
        journey?.collectionHandle;


      if (!collectionHandle) {
        return null;
      }


      const focusKey =
        `bohotales_focus_${collectionHandle}`;


      const focusRaw =
        sessionStorage.getItem(
          focusKey
        );


      if (!focusRaw) {
        return null;
      }


      let focus;


      try {

        focus =
          JSON.parse(focusRaw);

      } catch {

        return null;

      }


      if (
        !focus?.tag ||
        !focus?.expiresAt
      ) {

        return null;

      }


      /*
       Expired Focus
      */

      if (focus.expiresAt <= Date.now()) {

        sessionStorage.removeItem(
          focusKey
        );

        return null;

      }


      /*
       Defensive check.

       Your collection system already controls the 30-minute
       expiry. This prevents obviously stale/corrupt values
       from surviving unexpectedly.
      */

      if (
        focus.expiresAt >
        Date.now() + FOCUS_DURATION + 60000
      ) {

        console.warn(
          'BOHO YML — unusual Focus expiry detected.'
        );

      }


      return {
        collectionHandle,
        tag: focus.tag,
        expiresAt: focus.expiresAt
      };


    } catch (error) {

      console.warn(
        'BOHO YML — could not read Focus:',
        error
      );

      return null;

    }

  }



  /* =========================================================
     GET PRODUCTS BELONGING TO FOCUS TAG
     ========================================================= */

  async function getFocusProducts(
    collectionHandle,
    focusTag,
    currentProductId
  ) {

    try {

      const url =
        `/collections/` +
        `${encodeURIComponent(collectionHandle)}/` +
        `${encodeURIComponent(focusTag)}`;


      const response =
        await fetch(url, {
          credentials: 'same-origin'
        });


      if (!response.ok) {

        throw new Error(
          `Focus collection request failed: ${response.status}`
        );

      }


      const html =
        await response.text();


      const doc =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );


      /*
       Your collection implementation already exposes
       data-product-id on product-grid items.
      */

      const productItems =
        Array.from(
          doc.querySelectorAll(
            '.product-grid > .product-grid__item[data-product-id]'
          )
        );


      const seen =
        new Set();


      const products = [];


      for (const item of productItems) {

        const productId =
          String(item.dataset.productId || '');


        if (!productId) {
          continue;
        }


        /*
         Never recommend the product currently being viewed.
        */

        if (productId === currentProductId) {
          continue;
        }


        if (seen.has(productId)) {
          continue;
        }


        const link =
          item.querySelector(
            'a[href*="/products/"]'
          );


        if (!link) {
          continue;
        }


        const productUrl =
          new URL(
            link.href,
            window.location.origin
          );


        const match =
          productUrl.pathname.match(
            /^\/products\/([^/?#]+)/
          );


        if (!match) {
          continue;
        }


        seen.add(productId);


        products.push({
          id: productId,
          handle: match[1],
          url: `/products/${match[1]}`
        });

      }


      return products;


    } catch (error) {

      console.warn(
        'BOHO YML — Focus products failed:',
        error
      );


      /*
       Important:
       Focus failure must NOT break recommendations.
       Shopify recommendations will still be displayed.
      */

      return [];

    }

  }



  /* =========================================================
     RENDER ONE FOCUS PRODUCT USING LIQUID
     ========================================================= */

  async function renderProductCard(
    focusProduct,
    sectionId
  ) {

    try {

      const url =
        `${focusProduct.url}` +
        `?section_id=${encodeURIComponent(sectionId)}`;


      const response =
        await fetch(url, {
          credentials: 'same-origin'
        });


      if (!response.ok) {

        throw new Error(
          `Product card render failed: ${response.status}`
        );

      }


      const html =
        await response.text();


      const doc =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );


      const renderer =
        doc.querySelector(
          '[data-boho-yml-product-renderer]'
        );


      if (!renderer) {
        return null;
      }


      const card =
        renderer.querySelector(
          '[data-boho-yml-card]'
        );


      if (!card) {
        return null;
      }


      return card;


    } catch (error) {

      console.warn(
        `BOHO YML — could not render ${focusProduct.handle}:`,
        error
      );


      return null;

    }

  }



  /* =========================================================
     MERGE + DEDUPLICATE
     ========================================================= */

  function mergeCards({
    focusCards,
    shopifyCards,
    currentProductId,
    maxProducts
  }) {

    const finalCards = [];

    const seen =
      new Set();


    const candidates = [
      ...focusCards,
      ...shopifyCards
    ];


    for (const card of candidates) {

      const productId =
        String(
          card.dataset.productId || ''
        );


      if (!productId) {
        continue;
      }


      /*
       Never show current PDP.
      */

      if (productId === currentProductId) {
        continue;
      }


      /*
       Focus product may already exist in Shopify's list.
       Because Focus cards are processed first, the Shopify
       duplicate is discarded automatically.
      */

      if (seen.has(productId)) {
        continue;
      }


      seen.add(productId);

      finalCards.push(card);


      if (
        finalCards.length >= maxProducts
      ) {
        break;
      }

    }


    return finalCards;

  }



  /* =========================================================
     INITIALIZE
     ========================================================= */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  } else {

    init();

  }


  /*
   Shopify Theme Editor can dynamically insert sections.
  */

  document.addEventListener(
    'shopify:section:load',
    init
  );


})();